import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Alert
} from '@mui/material';
import axios from 'axios';
import { useToast } from '../../contexts/ToastContext';
import { useNavigate } from 'react-router-dom';

/**
 * Dialog for creating an intimation from AD/SB Service Bulletin.
 * The intimation appears in "Intimations to Create WO" tab on Maintenance page;
 * CA creates the work order from there and acknowledges.
 */
const CreateIntimationFromADSBulletinDialog = ({
  open,
  onClose,
  onSuccess,
  bulletin
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { showToast } = useToast();
  const navigate = useNavigate();

  const getAdsbNumber = (b) => {
    if (!b) return '';
    if (b.adsbBulletinNumber) return b.adsbBulletinNumber;
    if (b._id) return `ADSB-${b._id.toString().slice(-8).toLowerCase()}`;
    return '';
  };

  const handleCreate = async () => {
    if (!bulletin) return;

    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`/api/adsb-service-bulletins/${bulletin._id}/create-intimation`);
      showToast('Intimation created successfully', 'success');
      onSuccess();
      onClose();
      navigate('/maintenance', { state: { openIntimationsTab: true, intimationId: res.data._id } });
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to create intimation';
      setError(message);
      showToast(message, 'error');
    } finally {
      setLoading(false);
    }
  };

  if (!bulletin) return null;

  const aircraftSummary = (bulletin.aircraftAssemblies || [])
    .map(aa => aa.aircraftId?.registration || aa.aircraftId)
    .filter(Boolean)
    .join(', ') || '—';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create WO Intimation</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 2 }}>
          <Typography variant="body1" gutterBottom>
            <strong>AD/SB:</strong> {bulletin.title}
            {' '}
            <Box component="span" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
              ({getAdsbNumber(bulletin)})
            </Box>
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Aircraft:</strong> {aircraftSummary}
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            <strong>Checks:</strong> {bulletin.adsbChecks?.length ?? 0}
          </Typography>
          <Alert severity="info" sx={{ mt: 2 }}>
            An intimation will be created and linked to this AD/SB. Create the work order from the &quot;Intimations to Create WO&quot; tab on the Maintenance page and acknowledge with the WO number.
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={loading}
        >
          {loading ? 'Creating...' : 'Create WO Intimation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateIntimationFromADSBulletinDialog;
